import { Injectable } from '@angular/core';
import Dexie from 'dexie';
import moment from 'moment';
import { LOG_LEVEL } from '../../enums/log-level.enum';
import { LogEntry } from './log-entry';
import {
    EncryptionService,
    PREFIX_ENCRYPTED_CONTENT,
    PREFIX_ENCRYPTED_CONTENT_LEGACY,
    UpgradableEncryption,
} from '@evorait/pace-encryption';
import { firstValueFrom, from, Observable, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { EncryptionMigrator } from '../../../../../pace-encryption/src/lib/services/encryption-migrator.service';

@Injectable()
export class LogDexieService extends Dexie implements UpgradableEncryption {
    public static readonly DB_NAME = 'LogDatabase';
    public static readonly DB_TABLE = 'logentries';
    public static readonly DB_KEY_PATH = 'id';

    private static readonly VERSION_ONE = 1;
    private static readonly VERSION_TWO = LogDexieService.VERSION_ONE + 1;

    private logentries: Dexie.Table<LogEntry, number>;

    private checkedForOldEntries = false;
    private pendingEncryptionQueue: LogEntry[] = [];

    public constructor(
        private readonly encryption: EncryptionService,
        private readonly encryptionMigrator: EncryptionMigrator,
    ) {
        super(LogDexieService.DB_NAME);
        this.version(LogDexieService.VERSION_ONE).stores({
            [LogDexieService.DB_TABLE]: 'id++,timestamp,level,message',
        });

        // encrypt potentially existing data
        this.version(LogDexieService.VERSION_TWO).upgrade(async (trans) => {
            await firstValueFrom(this.encryption.selectInitialized.pipe(filter(Boolean)));
            const entriesToUpgrade = await trans
                .table(LogDexieService.DB_TABLE)
                .toCollection()
                .filter((entry) => !entry.message.startsWith(PREFIX_ENCRYPTED_CONTENT))
                .toArray();

            if (entriesToUpgrade.length > 0) {
                const encryptionResults = await Promise.allSettled(
                    entriesToUpgrade.map(async (entry: LogEntry) => {
                        await this.logentries.update(entry, {
                            message: await Dexie.waitFor(this.encryption.encryptAsync(entry.message)),
                        });
                    }),
                );

                const failed = encryptionResults.filter((r) => r.status === 'rejected');
                if (failed.length) {
                    console.error(this.constructor.name, `${failed.length} entries failed to encrypt...`);
                }
            }
        });

        this.logentries.mapToClass(LogEntry);
        this.handleEncryptionDelay();
    }

    /**
     *
     * Checks if there are any entries using legacy encryption format.
     *
     * This method queries the collection of stored entries and determines whether
     * any entry's content starts with the legacy encryption prefix.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` if at least one
     * entry uses legacy encryption, otherwise `false`.
     */
    public hasLegacyEncryption(): Observable<boolean> {
        return from(
            this.logentries
                .toCollection()
                .filter((entry) => entry.message.startsWith(PREFIX_ENCRYPTED_CONTENT_LEGACY))
                .count()
                .then((count) => count > 0),
        );
    }

    /**
     * Upgrades all entries from legacy encryption to the current encryption format.
     *
     * This method performs a read-write transaction on the collection of stored entries.
     * It finds all entries whose content starts with the legacy prefix,
     * re-encrypts their content using the `encryptionMigrator`, and updates the entries
     * in the database.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` once all legacy
     * encrypted entries have been successfully migrated.
     */
    public upgradeEncryption(): Observable<boolean> {
        return from(
            this.transaction('rw', this.logentries, async () => {
                const fileEntriesToMigrate = await this.logentries
                    .toCollection()
                    .filter((entry) => entry.message.startsWith(PREFIX_ENCRYPTED_CONTENT_LEGACY))
                    .toArray();

                const results = await Promise.allSettled(
                    fileEntriesToMigrate.map(async (entry: LogEntry) => {
                        const encrypted = await Dexie.waitFor(this.encryptionMigrator.reEncrypt(entry.message));
                        return this.logentries.update(entry, { message: encrypted });
                    }),
                );

                const failed = results.filter((r) => r.status === 'rejected');
                if (failed.length) {
                    console.error(this.constructor.name, `${failed.length} entries failed to migrate`);
                    throw new Error(`${failed.length} entries failed to migrate`);
                }
            }).then(() => true),
        );
    }

    /**
     * Waits for the encryption service to be initialised and
     * then encrypts and stores all log messages that could not be handled beforehand.
     */
    private handleEncryptionDelay(): void {
        this.encryption.selectInitialized.pipe(filter(Boolean), take(1)).subscribe(() => {
            this.pendingEncryptionQueue.forEach((logEntry) => {
                this.encryption
                    .encryptAsync(logEntry.message)
                    .then((message) => {
                        this.logentries.add({ ...logEntry, message }).catch((e) => {
                            console.error(
                                `${this.constructor.name}: Failed to add log entry to database: ${JSON.stringify(e)}`,
                            );
                        });
                    })
                    .catch((error) => {
                        console.error(this.constructor.name, 'Failed to encrypt log message async', error);
                    });
            });
            this.pendingEncryptionQueue = [];
        });
    }

    public get pendingQueueLength(): number {
        return this.pendingEncryptionQueue.length;
    }

    public async getAllLogEntries(maxLines?: number): Promise<LogEntry[]> {
        try {
            let collection = this.logentries.orderBy('timestamp').reverse();
            if (!!maxLines && maxLines > 0) {
                collection = collection.limit(maxLines);
            }

            const logEntries = await collection.toArray();
            for (const logEntry of logEntries) {
                logEntry.message = await this.encryption.decryptAsync(logEntry.message);
            }
            return logEntries;
        } catch (error) {
            console.error(`LogDexieService: getAllLogEntries: Error on reading log entries from database: ${error}`);
            return [];
        }
    }

    /**
     * Writes a single log message to the database. The message should contain any required information in a single string.
     *
     * @param message the log message to be written
     * @param level the log level of the message
     * @returns a promise that resolves when the message has been written to the database
     */
    public async writeEntry(message: string, level: LOG_LEVEL): Promise<void> {
        let start = Promise.resolve();
        const dexieLogRetainingPeriod = 5;
        if (!this.checkedForOldEntries) {
            start = this.purgeEntriesOlderThanDays(dexieLogRetainingPeriod).then(() => {
                this.checkedForOldEntries = true;
            });
        }
        await start;
        if (!this.encryption.isInitialized) {
            this.pendingEncryptionQueue.push({
                timestamp: moment().toDate().getTime(),
                level: level,
                message: message,
            });
            return;
        }
        message = await this.encryption.encryptAsync(message);
        try {
            await this.logentries.add({
                timestamp: moment().toDate().getTime(),
                level: level,
                message: message,
            });
        } catch (error) {
            console.error(`LogDexieService: writeEntry: Error on writing log entry to database: ${error}`);
        }
    }

    /**
     * Deletes all log entries from the database. Especially used for unit tests to produce a well defined starting state.
     *
     * @returns {Promise<void>} A promise that resolves when the entries have been cleared.
     */
    public async clearEntries(): Promise<void> {
        try {
            return await this.logentries.clear();
        } catch (error) {
            console.error(`LogDexieService: clearEntries: Error on clearing the database: ${error}`);
        }
    }

    public purgeEntriesOlderThanDays(days: number): Promise<void> {
        return new Promise((resolve, reject) => {
            this.logentries
                .where('timestamp')
                .belowOrEqual(this.convertDaysToMilliseconds(days))
                .delete()
                .then(() => {
                    resolve();
                })
                .catch((error) => {
                    const errorMessage = `LogDexieService: purgeEntriesOlderThanDays: Could not delete entries from database older than: ${days}. Error ${error}`;
                    reject(errorMessage);
                });
        });
    }

    public convertDaysToMilliseconds(days: number): number {
        // Used toDate().getTime() here on purpose to allow unit tests to manipulate the clock.
        const now = moment().toDate().getTime();
        const daysInMilliseconds = moment.duration(days, 'day').asMilliseconds();
        return now - daysInMilliseconds;
    }
}
